/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { SocialNetType } from 'modules/organizer/domain/interfaces/social-net';
import { upsertSocialNets } from 'modules/organizer/store/social-nets';
import useSocialNets from 'modules/organizer/ui/hooks/use-social-nets';
import SocialNetForm, { InitialValues } from 'modules/settings/ui/components/social-net-components/form/social-net-form';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import type { AppDispatch } from 'store/store';
import type { DivProps } from 'typings';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

interface IProps extends DivProps {}

function SocialNet(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { isLoading, socialNet } = useSocialNets();
  // Мемо по `socialNet`: у формы `enableReinitialize`, и новый литерал на перерисовке от `upsertSocialNets.pending`
  // сбрасывал её посреди сабмита — «Сохранить» снова активна, в полях старые ссылки (IMGM-1561).
  const initialValue = useMemo(
    () => ({
      website: socialNet.find((item) => item.type === SocialNetType.TOURNAMENT_SITE)?.url?.toString(),
      vk: socialNet.find((item) => item.type === SocialNetType.VK)?.url?.toString(),
      'vk-video': socialNet.find((item) => item.type === SocialNetType.VK_VIDEO)?.url?.toString(),
      telegram: socialNet.find((item) => item.type === SocialNetType.TELEGRAM)?.url?.toString(),
      youtube: socialNet.find((item) => item.type === SocialNetType.YOUTUBE)?.url?.toString(),
      instagram: socialNet.find((item) => item.type === SocialNetType.INSTAGRAM)?.url?.toString(),
    }),
    [socialNet],
  );

  const onSubmit = useCallback(
    // Промис — форме: «Сохранить» заперта до ответа (IMGM-1561).
    (values: InitialValues) => {
      return dispatch(upsertSocialNets(values))
        .unwrap()
        .then(() => {
          toast.success(t`Ссылки успешно сохранены`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось сохранить ссылки`);
        });
    },
    [dispatch, t, toastApiError],
  );

  return <SocialNetForm initialValues={initialValue} onSubmit={onSubmit} loader={isLoading} />;
}

export default SocialNet;
